import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  X,
  ArrowRight,
  MessageSquare,
  PenLine,
  TrendingUp,
  Brain,
  Shield,
  Target,
  Sparkles
} from 'lucide-react';

const comparisonData = [
  { feature: 'AI Life Coach', vzmo: true, headspace: false, calm: false },
  { feature: 'Personalized Guidance', vzmo: true, headspace: false, calm: false },
  { feature: 'Remembers Your Context', vzmo: true, headspace: false, calm: false },
  { feature: 'Private Journaling', vzmo: true, headspace: true, calm: false },
  { feature: 'Action Plan Creation', vzmo: true, headspace: false, calm: false },
  { feature: 'Weekly Progress Tracking', vzmo: true, headspace: true, calm: true },
  { feature: 'Career Coaching', vzmo: true, headspace: false, calm: false },
  { feature: 'Relationship Guidance', vzmo: true, headspace: false, calm: false },
  { feature: 'Guided Meditation', vzmo: false, headspace: true, calm: true },
  { feature: 'Sleep Stories', vzmo: false, headspace: true, calm: true },
  { feature: '24/7 Availability', vzmo: true, headspace: true, calm: true },
  { feature: 'No Subscription Lock-in', vzmo: true, headspace: false, calm: false },
];

const testimonials = [
  {
    quote: "I used Headspace for 2 years but never felt like it addressed my actual problems. VZMO gives me real advice I can act on.",
    author: "Sarah M.",
    role: "Marketing Manager",
    switched: "Switched from Headspace"
  },
  {
    quote: "Meditation helped me relax but didn't help me make decisions. Now I have a coach that actually knows my situation.",
    author: "James T.",
    role: "Software Engineer",
    switched: "Former Calm user"
  },
  {
    quote: "The AI remembers everything. I don't have to re-explain my career goals every session like I did with other apps.",
    author: "Michelle K.",
    role: "Freelance Designer",
    switched: "Tried 5+ apps before VZMO"
  }
];

const switchReasons = [
  {
    icon: Brain,
    title: "More Than Meditation",
    description: "Headspace helps you relax. VZMO helps you figure out what to do next. Get real guidance on career, relationships, and life decisions."
  },
  {
    icon: MessageSquare,
    title: "A Coach That Remembers",
    description: "Your AI coach knows your history, goals, and challenges. No starting from scratch every session. Real continuity, real progress."
  },
  {
    icon: Target,
    title: "Actionable Plans",
    description: "Move beyond vague intentions. Get specific action plans with weekly check-ins to actually achieve your goals."
  },
  {
    icon: Shield,
    title: "Privacy First",
    description: "No social features, no public profiles. Just you and your thoughts. Everything is encrypted and private."
  }
];

export function HeadspaceAlternativePage() {
  useEffect(() => {
    document.title = 'Headspace Alternative for Life Coaching | VZMO';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Looking for a Headspace alternative that goes beyond meditation? VZMO offers AI life coaching, personalized guidance, and action plans. Try free for 7 days.');
    }
  }, []);

  return (
    <div className="bg-white pt-20">
      <section className="py-16 sm:py-24 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="inline-block text-coral-400 font-medium text-sm tracking-wide uppercase mb-4">
              Headspace Alternative
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight">
              More than meditation.
              <span className="block text-coral-400">Real guidance.</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed mb-8">
              Headspace is great for relaxation. But when you need actual advice on career moves,
              relationship challenges, or life decisions - you need a coach, not a meditation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center px-8 py-4 bg-coral-500 text-white rounded-full font-semibold text-lg hover:bg-coral-600 transition-all shadow-lg"
              >
                Try VZMO free for 7 days
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <a
                href="#comparison"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white rounded-full font-semibold text-lg hover:bg-white/20 transition-all"
              >
                See the difference
              </a>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-16">
            {[
              { icon: PenLine, label: 'Journal', desc: 'Process your thoughts' },
              { icon: MessageSquare, label: 'Coach', desc: 'Get real advice' },
              { icon: TrendingUp, label: 'Track', desc: 'See your progress' }
            ].map((item, index) => (
              <div key={index} className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10 text-center">
                <div className="w-12 h-12 rounded-xl bg-coral-500/20 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-6 w-6 text-coral-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">{item.label}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Why people switch from Headspace
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Meditation apps help you relax. VZMO helps you take action.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {switchReasons.map((reason, index) => (
              <div key={index} className="bg-[#FDFBF7] rounded-2xl p-8 border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-coral-100 flex items-center justify-center mb-5">
                  <reason.icon className="h-6 w-6 text-coral-600" />
                </div>
                <h3 className="text-xl font-heading font-bold text-slate-900 mb-3">
                  {reason.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {reason.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="comparison" className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Feature comparison
            </h2>
            <p className="text-lg text-slate-600">
              See how VZMO stacks up against meditation apps
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-4 px-6 font-semibold text-slate-900">Feature</th>
                    <th className="text-center py-4 px-4 font-semibold text-coral-600">VZMO</th>
                    <th className="text-center py-4 px-4 font-semibold text-slate-500">Headspace</th>
                    <th className="text-center py-4 px-4 font-semibold text-slate-500">Calm</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <td className="py-4 px-6 text-slate-700">{row.feature}</td>
                      <td className="py-4 px-4 text-center">
                        {row.vzmo ? (
                          <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center mx-auto">
                            <Check className="h-4 w-4 text-sage-600" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
                            <X className="h-4 w-4 text-slate-400" />
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {row.headspace ? (
                          <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center mx-auto">
                            <Check className="h-4 w-4 text-sage-600" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
                            <X className="h-4 w-4 text-slate-400" />
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {row.calm ? (
                          <div className="w-6 h-6 rounded-full bg-sage-100 flex items-center justify-center mx-auto">
                            <Check className="h-4 w-4 text-sage-600" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
                            <X className="h-4 w-4 text-slate-400" />
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-center text-sm text-slate-500 mt-6">
            Comparison based on publicly available features as of 2024. Features may change.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              What switchers say
            </h2>
            <p className="text-lg text-slate-600">
              Real feedback from people who made the switch
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-[#FDFBF7] rounded-2xl p-8 border border-slate-100">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Sparkles key={i} className="h-4 w-4 text-coral-400" />
                  ))}
                </div>
                <blockquote className="text-slate-700 leading-relaxed mb-6">
                  "{testimonial.quote}"
                </blockquote>
                <div>
                  <p className="font-semibold text-slate-900">{testimonial.author}</p>
                  <p className="text-sm text-slate-500">{testimonial.role}</p>
                  <p className="text-xs text-coral-600 mt-1">{testimonial.switched}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Common questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: "I love Headspace. Why would I switch?",
                a: "You don't have to choose! Many users keep Headspace for meditation and use VZMO for coaching. But if you're looking for more than relaxation - actual guidance on decisions, goals, and challenges - VZMO fills that gap."
              },
              {
                q: "Is this just ChatGPT with a different skin?",
                a: "No. VZMO's coach remembers your entire history, understands your goals, and creates personalized action plans. It's built specifically for personal growth, not general Q&A. Plus, your data stays private and isn't used to train models."
              },
              {
                q: "Can VZMO help with anxiety like Headspace does?",
                a: "VZMO helps you understand what's causing your anxiety and take action to address it. It's coaching, not therapy - but many users find that solving the underlying problems helps more than just managing symptoms."
              },
              {
                q: "What if I want meditation too?",
                a: "VZMO focuses on coaching and journaling. If you love guided meditation, keep your meditation app! VZMO works great alongside Headspace or Calm as your action-oriented companion."
              },
              {
                q: "How much does it cost compared to Headspace?",
                a: "VZMO starts at $9.99/month after a 7-day free trial. Headspace is $12.99/month. More importantly, you can cancel anytime - no annual commitment required."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
            Ready to try something different?
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            7-day free trial. No credit card required. Cancel anytime.
          </p>

          <Link
            to="/signup"
            className="inline-flex items-center px-10 py-4 bg-coral-500 text-white rounded-full font-semibold text-lg hover:bg-coral-600 transition-all shadow-lg"
          >
            Start your free trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>

          <p className="text-slate-400 text-sm mt-6">
            Join thousands who've upgraded from meditation to coaching
          </p>
        </div>
      </section>
    </div>
  );
}
